// Quack, this is a duck :) https://github.com/erikras/ducks-modular-redux
import { combineReducers } from 'redux';
import values from 'lodash/values';
import queryString from 'query-string';
import { get, post, del } from '../../shared/http/request';
import { handleApiError } from '../../shared/http/error';
import { toastr } from 'react-redux-toastr';

/** ************************
 * Runs redux bundle
 ************************ */
// API
const BASE_URL = '/api/runs/';
const BASE_COMMAND_URL = '/api/command/';

// Actions
const LOAD_RUN_PENDING = 'Fetching run';
const LOAD_RUN_FULFILLED = 'Run fetched';
const LOAD_RUN_REJECTED = 'Failed to fetch run';
const LOAD_RUNS_PENDING = 'Fetching runs';
const LOAD_RUNS_FULFILLED = 'Runs fetched';
const LOAD_RUNS_REJECTED = 'Failed to fetch runs';
const CLEAR_ERROR_MESSAGE = 'Cleared runs error message';

// Saved search
const STORE_SEARCH_RUNS = 'Stored run search form';

// Manual location
const CREATE_LOCATION_PENDING = 'Creating manual location';
const CREATE_LOCATION_FULFILLED = 'Manual location created';
const CREATE_LOCATION_REJECTED = 'Failed to create manual location';
const UPDATE_LOCATION_PENDING = 'Updating manual location';
const UPDATE_LOCATION_FULFILLED = 'Manual location updated';
const UPDATE_LOCATION_REJECTED = 'Failed to update manual location';
const DELETE_LOCATION_PENDING = 'Deleting manual location';
const DELETE_LOCATION_FULFILLED = 'Manual location deleted';
const DELETE_LOCATION_REJECTED = 'Failed to delete manual location';

// Reducers
const entities = (state = null, action) => {
    switch (action.type) {
        case LOAD_RUN_FULFILLED:
            const runById = [{ [`${action.run.runId}_DETAILS`]: action.run }];
            return Object.assign({}, state, ...runById);
        case LOAD_RUNS_FULFILLED:
            const runsById = action.runs.map(run => ({ [run.runId]: run }));
            return Object.assign({}, state, ...runsById);
        case CREATE_LOCATION_FULFILLED:
            // Update optimistically?
            // const currentRun = state[action.runId];
            // let { gpsPoints = [] } = currentRun;
            // const newRun = Object.assign({}, state[action.runId], { gpsPoints: [...gpsPoints, action.gpsPoint] });
            return state;
        case LOAD_RUNS_PENDING:
        case LOAD_RUNS_REJECTED:
            return {};
        default:
            return state;
    }
};

const isFetching = (state = false, action) => {
    switch (action.type) {
        case LOAD_RUN_PENDING:
        case LOAD_RUNS_PENDING:
            return true;
        case LOAD_RUN_FULFILLED:
        case LOAD_RUN_REJECTED:
        case LOAD_RUNS_FULFILLED:
        case LOAD_RUNS_REJECTED:
            return false;
        default:
            return state;
    }
};

const isPending = (state = false, action) => {
    switch (action.type) {
        case CREATE_LOCATION_PENDING:
            return true;
        case CREATE_LOCATION_FULFILLED:
        case CREATE_LOCATION_REJECTED:
            return false;
        default:
            return state;
    }
};

const isLocationUpdatePending = (state = false, action) => {
    switch (action.type) {
        case UPDATE_LOCATION_PENDING:
            return true;
        case UPDATE_LOCATION_FULFILLED:
        case UPDATE_LOCATION_REJECTED:
            return false;
        default:
            return state;
    }
};

const isLocationDeletePending = (state = false, action) => {
    switch (action.type) {
        case DELETE_LOCATION_PENDING:
            return true;
        case DELETE_LOCATION_FULFILLED:
        case DELETE_LOCATION_REJECTED:
            return false;
        default:
            return state;
    }
};

const error = (state = null, action) => {
    switch (action.type) {
        case LOAD_RUN_REJECTED:
        case LOAD_RUNS_REJECTED:
        case CREATE_LOCATION_REJECTED:
            return action.message;
        case LOAD_RUN_PENDING:
        case LOAD_RUN_FULFILLED:
        case LOAD_RUNS_PENDING:
        case LOAD_RUNS_FULFILLED:
        case CLEAR_ERROR_MESSAGE:
        case CREATE_LOCATION_FULFILLED:
            return null;
        default:
            return state;
    }
};

export const INITIAL_RUN_SEARCH_FORM = {
    routeCode: '',
    runSequence: '',
    runDate: '',
    logisticPartner: '',
    runStatus: '',
    sortParam: '',
    sortDirection: '',
};
const runsSearchForm = (state = INITIAL_RUN_SEARCH_FORM, action) => {
    switch (action.type) {
        case STORE_SEARCH_RUNS:
            return Object.assign({}, state, action.search);
        default:
            return state;
    }
};

export default combineReducers({
    entities,
    isFetching,
    isPending,
    isLocationUpdatePending,
    isLocationDeletePending,
    runsSearchForm,
    error,
});

// Selectors
export const getRunById = (state, id) =>
    state.entities.runs.entities ? state.entities.runs.entities[`${id}_DETAILS`] : null;
export const getRuns = state => values(state.entities.runs.entities);
export const getIds = state => state.entities.runs.entities.ids;
export const getIsFetching = state => state.entities.runs.isFetching;
export const getError = state => state.entities.runs.error;
export const getRunSearchForm = state => state.entities.runs.runsSearchForm;
export const getLocationCreateIsPending = state => state.entities.runs.isPending;
export const getIsLocationUpdatePending = state => state.entities.runs.isLocationUpdatePending;
export const getIsLocationDeletePending = state => state.entities.runs.isLocationDeletePending;

// Action Creators/Thunks
export function loadRun(id) {
    return function(dispatch) {
        dispatch({ type: LOAD_RUN_PENDING });

        return get(`${BASE_URL}${id}`).then(
            response => dispatch({ type: LOAD_RUN_FULFILLED, run: response }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_RUN_REJECTED,
                    message: `${error.message}`,
                })
        );
    };
}

export function loadRunForGeneric(id) {
    return function(dispatch) {
        dispatch({ type: LOAD_RUN_PENDING });

        return get(`/api/abnormalities/generic/runs/${id}`).then(
            response => dispatch({ type: LOAD_RUN_FULFILLED, run: response }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_RUN_REJECTED,
                    message: `${error.message}`,
                })
        );
    };
}

export function silentFetchRun(id) {
    return function(dispatch) {
        return get(`${BASE_URL}${id}`).then(
            response => dispatch({ type: LOAD_RUN_FULFILLED, run: response }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_RUN_REJECTED,
                    message: `${error.message}`,
                })
        );
    };
}

export function loadRuns(params = null) {
    return function(dispatch) {
        let url = `${BASE_URL}`;

        if (params) {
            url += `?${queryString.stringify(params)}`;
        }

        dispatch({ type: LOAD_RUNS_PENDING });
        return get(url).then(
            response =>
                dispatch({
                    type: LOAD_RUNS_FULFILLED,
                    runs: response.runResources,
                }),
            error =>
                handleApiError(error, dispatch, {
                    type: LOAD_RUNS_REJECTED,
                    message: error.message,
                })
        );
    };
}

export function storeSearch(search) {
    return function(dispatch) {
        dispatch({ type: STORE_SEARCH_RUNS, search });
    };
}

export function clearErrorMessage() {
    return function(dispatch) {
        dispatch({ type: CLEAR_ERROR_MESSAGE });
        toastr.removeByType('error');
    };
}

export function createManualLocation(GPS_POINT) {
    return function(dispatch) {
        dispatch({ type: CREATE_LOCATION_PENDING });

        return post(`${BASE_COMMAND_URL}insertManualGps`, GPS_POINT).then(
            response =>
                dispatch({
                    type: CREATE_LOCATION_FULFILLED,
                    gpsPoint: response,
                    runId: response.runId,
                }),
            error =>
                handleApiError(error, dispatch, {
                    type: CREATE_LOCATION_REJECTED,
                    message: `${error.message}`,
                })
        );
    };
}

export function deleteManualLocation(data){
    return function(dispatch) {
        dispatch({ type: DELETE_LOCATION_PENDING });

        return del(`${BASE_COMMAND_URL}deleteManualGps` , data).then(
            () =>
                setTimeout(
                    () =>
                        dispatch({
                            type: DELETE_LOCATION_FULFILLED,
                        }),
                    1000
                ),
            error =>
                handleApiError(error, dispatch, {
                    type: DELETE_LOCATION_REJECTED,
                    message: `${error.message}`,
                    error: true,
                })
        );
    };
}

export function updateLocationManually(data) {
    return function(dispatch) {
        dispatch({ type: UPDATE_LOCATION_PENDING });

        return post(`${BASE_COMMAND_URL}updateLocationManually`, data).then(
            () =>
                setTimeout(
                    () =>
                        dispatch({
                            type: UPDATE_LOCATION_FULFILLED,
                        }),
                    1000
                ),
            error =>
                handleApiError(error, dispatch, {
                    type: UPDATE_LOCATION_REJECTED,
                    message: `${error.message}`,
                    error: true,
                })
        );
    };
}
